import React, { useEffect, useState } from "react";
import { Card, Empty, Field, StageChip } from "../../dashboard/primitives";
import { ImageGalleryUploader } from "../../dashboard/ImageUploader";
import {
  archiveProduct,
  createProduct,
  listAdminProducts,
  updateProduct,
} from "../../lib/catalog";
import { SkeletonList, SkeletonRow } from "../../components/Skeleton";
import { useMinLoading } from "../../lib/useMinLoading";

const CATEGORIES = [
  { value: "merch", label: "Merch (physical)" },
  { value: "subscription", label: "Subscription plan" },
  { value: "service", label: "Service / add-on" },
];
const SHIPPING = [
  { value: "none", label: "No shipping (digital)" },
  { value: "sticker", label: "Sticker / flat ($4)" },
  { value: "apparel", label: "Apparel ($8)" },
  { value: "heavy", label: "Bulky ($14)" },
];
const INTERVALS = [
  { value: "", label: "One-time" },
  { value: "month", label: "Monthly" },
  { value: "year", label: "Yearly" },
];

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const emptyForm = {
  category: "merch",
  name: "",
  description: "",
  images: [],
  priceDollars: "",
  interval: "",
  priceLabel: "",
  sizes: "",
  shippingClass: "sticker",
  inventory: "",
  published: true,
};

const Editor = ({ form, setForm, onSubmit, onCancel, submitting, error, mode }) => {
  const change = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const isMerch = form.category === "merch";
  const isSub = form.category === "subscription";

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} style={{ display: "grid", gap: 18 }}>
      <div className="fw-stack-sm" style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 12 }}>
        <Field label="Category">
          <select className="fw-select" value={form.category} onChange={change("category")} disabled={mode === "edit"}>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </Field>
        <Field label="Name">
          <input className="fw-input" value={form.name} onChange={change("name")} placeholder="bboy sticker pack" required />
        </Field>
      </div>

      <Field label="Description">
        <textarea className="fw-textarea" rows={2} value={form.description} onChange={change("description")} />
      </Field>

      <div className="fw-stack-sm" style={{ display: "grid", gridTemplateColumns: "160px 160px 1fr", gap: 12 }}>
        <Field label={isSub ? "Price / period (USD)" : "Price (USD)"}>
          <input className="fw-input" type="number" step="0.01" value={form.priceDollars} onChange={change("priceDollars")} placeholder="8.00" required={mode === "create"} />
        </Field>
        <Field label="Billing">
          <select className="fw-select" value={form.interval} onChange={change("interval")}>
            {INTERVALS.filter((i) => (isSub ? i.value : true)).map((i) => (
              <option key={i.value} value={i.value}>{i.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Price label (optional)">
          <input className="fw-input" value={form.priceLabel} onChange={change("priceLabel")} placeholder="Pro / month" />
        </Field>
      </div>
      {mode === "edit" && (
        <div style={{ fontSize: 12, color: "var(--fw-muted)", marginTop: -8 }}>
          Stripe prices are immutable — entering a new price adds one; leave blank to keep existing.
        </div>
      )}

      {isMerch && (
        <div className="fw-stack-sm" style={{ display: "grid", gridTemplateColumns: "1fr 200px 140px", gap: 12 }}>
          <Field label="Size options (comma-separated)">
            <input className="fw-input" value={form.sizes} onChange={change("sizes")} placeholder="S, M, L, XL" />
          </Field>
          <Field label="Shipping class">
            <select className="fw-select" value={form.shippingClass} onChange={change("shippingClass")}>
              {SHIPPING.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Inventory (blank = ∞)">
            <input className="fw-input" type="number" value={form.inventory} onChange={change("inventory")} />
          </Field>
        </div>
      )}

      <Field label="Images">
        <ImageGalleryUploader
          value={form.images}
          onChange={(images) => setForm((f) => ({ ...f, images }))}
          folder="uploads/products"
        />
      </Field>

      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
        <input type="checkbox" checked={!!form.published} onChange={change("published")} />
        Published — visible for sale
      </label>

      {error && <div style={{ color: "var(--fw-red)", fontSize: 13 }}>{error}</div>}

      <div style={{ display: "flex", gap: 8, paddingTop: 8, borderTop: "1px solid var(--fw-line)" }}>
        <button type="submit" className="fw-btn fw-btn--primary" disabled={submitting}>
          {submitting ? "Saving…" : mode === "create" ? "Create product" : "Save changes"}
        </button>
        <button type="button" className="fw-btn fw-btn--ghost" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

const ProductsSection = () => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    try { setItems(await listAdminProducts()); }
    catch (e) { setError(e?.response?.data?.error || e.message); setItems([]); }
  };
  useEffect(() => { load(); }, []);
  const showSkeleton = useMinLoading(items === null);

  const mode = editing === "new" ? "create" : editing ? "edit" : null;
  const startNew = () => { setError(null); setForm(emptyForm); setEditing("new"); };
  const startEdit = (p) => {
    setError(null);
    setForm({
      ...emptyForm,
      category: p.category,
      name: p.name,
      description: p.description || "",
      images: p.images || [],
      priceDollars: "",
      interval: "",
      priceLabel: "",
      sizes: (p.variants?.[0]?.options || []).join(", "),
      shippingClass: p.shippingClass || "none",
      inventory: p.inventory ?? "",
      published: p.published !== false,
    });
    setEditing(p.id);
  };

  const toPayload = (f) => {
    const payload = {
      category: f.category,
      name: f.name,
      description: f.description,
      images: f.images,
      shippingClass: f.category === "merch" ? f.shippingClass : "none",
      inventory: f.inventory === "" ? null : Number(f.inventory),
      published: !!f.published,
      variants:
        f.category === "merch" && f.sizes.trim()
          ? [{ name: "Size", options: f.sizes.split(",").map((s) => s.trim()).filter(Boolean) }]
          : [],
    };
    if (f.priceDollars !== "" && f.priceDollars != null) {
      payload.prices = [
        {
          unitAmount: Number(f.priceDollars),
          interval: f.interval || undefined,
          label: f.priceLabel || undefined,
        },
      ];
    }
    return payload;
  };

  const save = async () => {
    setError(null); setSubmitting(true);
    try {
      const payload = toPayload(form);
      if (mode === "create") {
        if (!payload.prices?.length) throw new Error("A price is required.");
        await createProduct(payload);
      } else {
        await updateProduct(editing, payload);
      }
      setEditing(null); setForm(emptyForm); await load();
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setSubmitting(false); }
  };

  const archive = async (p) => {
    if (!window.confirm(`Archive "${p.name}"? It will be hidden and deactivated in Stripe.`)) return;
    await archiveProduct(p.id); await load();
  };

  return (
    <Card
      title={editing ? (mode === "create" ? "New product" : `Editing · ${form.name}`) : `Products & plans (${items?.length ?? 0})`}
      action={editing ? null : <button className="fw-btn fw-btn--primary" onClick={startNew}>New product</button>}
    >
      {editing ? (
        <Editor form={form} setForm={setForm} onSubmit={save} onCancel={() => setEditing(null)} submitting={submitting} error={error} mode={mode} />
      ) : showSkeleton ? (
        <SkeletonList count={4} ariaLabel="Loading products"><SkeletonRow cols={4} widths={["1fr", "120px", "100px", "120px"]} /></SkeletonList>
      ) : !items?.length ? (
        <Empty>No products yet — add stickers, tees, or a subscription plan.</Empty>
      ) : (
        <div>
          {items.map((p) => {
            const price = (p.prices || []).find((x) => x.stripePriceId === p.defaultPriceId) || p.prices?.[0];
            return (
              <div className="fw-stack-sm" key={p.id} style={{ display: "grid", gridTemplateColumns: "48px 1fr auto auto auto", gap: 12, alignItems: "center", padding: "14px 0", borderBottom: "1px solid var(--fw-line)" }}>
                <div style={{ width: 44, height: 44, borderRadius: 6, background: "var(--fw-cream-2)", overflow: "hidden", border: "1px solid var(--fw-line)" }}>
                  {p.images?.[0] && <img src={p.images[0]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                  <div style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)", marginTop: 2 }}>
                    {p.category}{price ? ` · ${money(price.unitAmount)}${price.recurring ? `/${price.recurring.interval}` : ""}` : ""}{p.category === "merch" && p.shippingClass !== "none" ? ` · ships ${p.shippingClass}` : ""}
                  </div>
                </div>
                <StageChip status={p.published && p.active !== false ? "paid" : "pending"} />
                <button className="fw-btn fw-btn--ghost" style={{ padding: "6px 10px" }} onClick={() => startEdit(p)}>Edit</button>
                <button className="fw-btn fw-btn--ghost" style={{ padding: "6px 10px", color: "var(--fw-red)", borderColor: "var(--fw-red)" }} onClick={() => archive(p)}>Archive</button>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default ProductsSection;
